"use client";

import { useState, useMemo } from "react";
import { Button } from "@repo/ui";
import { getNextCohort } from "@repo/constants/between-sessions";
import {
  X,
  ArrowRight,
  ArrowLeft,
  GraduationCap,
  CheckCircle,
  AlertCircle,
  Users,
  Heart,
  MapPin,
  Briefcase,
  Sparkles,
} from "lucide-react";

type QuizStep = 'intro' | 'main-question' | 'fit-assessment' | 'community-result' | 'core-result' | 'placement-result';

interface PathQuizModalProps {
  isOpen: boolean;
  onClose: () => void;
  airtableFormUrl: string;
}

export default function PathQuizModal({ isOpen, onClose, airtableFormUrl }: PathQuizModalProps) {
  const [step, setStep] = useState<QuizStep>('intro');
  const [fitChecklist, setFitChecklist] = useState<Record<string, boolean>>({});

  const nextCohort = useMemo(() => getNextCohort(), []);

  const handleClose = () => {
    setStep('intro');
    setFitChecklist({});
    onClose();
  };

  const handleOptionClick = (option: 'exploring-alli' | 'community-only' | 'supervision-plus') => {
    if (option === 'exploring-alli') {
      setStep('fit-assessment');
    } else if (option === 'community-only') {
      setStep('core-result');
    } else if (option === 'supervision-plus') {
      setStep('community-result');
    }
  };

  const toggleFitItem = (item: string) => {
    setFitChecklist(prev => ({
      ...prev,
      [item]: !prev[item]
    }));
  };

  const goodFitItems = [
    "Are passionate about increasing access to therapy",
    "Have some real-world clinical experience",
    "Believe in long-term therapy",
    "Are open to practicing with a wide range of modalities",
    "Want a varied caseload",
    "Are comfortable with marketing and self-promotion",
    "Feel ready to build and keep a caseload",
    "Enjoy working in a fully virtual, tech-based clinic",
    "Can commit 25+ hours per week",
  ];

  const notFitItems = [
    "Prefer a slower-paced environment",
    "Have limited time available",
    "Cannot do daytime EST hours for supervision",
    "Are passionate about supporting a niche or specific population",
    "Prefer in-person work over 100% virtual",
    "Want to practice in a specific modality only",
    "Do not yet have clinical experience",
    "Prefer short-term therapy",
    "Are not comfortable using and learning new technology",
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] mx-4 bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-scale-in">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6 md:p-8">
          {/* Intro Step */}
          {step === 'intro' && (
            <div className="text-center animate-fade-in">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Hi Future Intern! 👋
              </h2>

              <p className="text-lg text-gray-600 mb-6">
                Getting ready for practicum placement?
              </p>

              <div className="bg-purple-50 rounded-2xl p-6 mb-8">
                <p className="text-gray-700 leading-relaxed">
                  Let's find the right path for you. Whether you're looking for a full placement
                  with Alli or want community support alongside another site, we've got options
                  that fit your needs.
                </p>
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-purple-600 mb-8">
                <Sparkles className="w-4 h-4" />
                <span>Now accepting for <strong>{nextCohort.name}</strong> starting {nextCohort.displayDate}</span>
              </div>

              <Button
                size="lg"
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-lg font-semibold"
                onClick={() => setStep('main-question')}
              >
                Let's Go
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}

          {/* Main Question Step */}
          {step === 'main-question' && (
            <div className="animate-fade-in">
              <button
                onClick={() => setStep('intro')}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back</span>
              </button>

              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 text-center">
                What are you looking for?
              </h2>
              <p className="text-gray-500 text-center mb-8">Select the option that best describes you</p>

              <div className="space-y-4">
                {/* Option 1: Exploring Alli */}
                <button
                  onClick={() => handleOptionClick('exploring-alli')}
                  className="w-full group bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 hover:border-blue-300 rounded-2xl p-6 text-left transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Briefcase className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        I'm exploring fit for a placement with Alli
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Learn about what makes Alli unique and see if it's the right match for your practicum
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-blue-500 mt-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>

                {/* Option 2: Community Only */}
                <button
                  onClick={() => handleOptionClick('community-only')}
                  className="w-full group bg-violet-50 hover:bg-violet-100 border-2 border-violet-200 hover:border-violet-300 rounded-2xl p-6 text-left transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-violet-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        I have a placement somewhere else and want added support
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Perfect for students who still want the support, resources, and hands-on training to become a great therapist, while moving alongside a cohort of interns at the same stage.
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-violet-500 mt-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>

                {/* Option 3: Community + Supervision */}
                <button
                  onClick={() => handleOptionClick('supervision-plus')}
                  className="w-full group bg-rose-50 hover:bg-rose-100 border-2 border-rose-200 hover:border-rose-300 rounded-2xl p-6 text-left transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-rose-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Heart className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        I'm looking for added peer community and group supervision
                      </h3>
                      <p className="text-gray-600 text-sm">
                        All the support, resources, and hands-on training plus group supervision and structured peer group alongside your other placement
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-rose-500 mt-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Fit Assessment Step */}
          {step === 'fit-assessment' && (
            <div className="animate-fade-in">
              <button
                onClick={() => setStep('main-question')}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back</span>
              </button>

              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Is Alli the Right Fit?
                </h2>
                <p className="text-gray-600">
                  Check the boxes that describe you to see if we might be a match
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Great Fit */}
                <div className="bg-green-50 rounded-2xl p-5 border border-green-200">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <h3 className="font-semibold text-gray-900">Likely a great fit if you...</h3>
                  </div>
                  <div className="space-y-2">
                    {goodFitItems.map((item, i) => (
                      <label
                        key={`good-${i}`}
                        className="flex items-start gap-3 cursor-pointer group"
                      >
                        <input
                          type="checkbox"
                          checked={fitChecklist[`good-${i}`] || false}
                          onChange={() => toggleFitItem(`good-${i}`)}
                          className="mt-1 w-4 h-4 text-green-600 rounded border-green-300 focus:ring-green-500"
                        />
                        <span className="text-sm text-gray-700 group-hover:text-gray-900">{item}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* May Not Be Fit */}
                <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <h3 className="font-semibold text-gray-900">May not be the right fit if you...</h3>
                  </div>
                  <div className="space-y-2">
                    {notFitItems.map((item, i) => (
                      <label
                        key={`not-${i}`}
                        className="flex items-start gap-3 cursor-pointer group"
                      >
                        <input
                          type="checkbox"
                          checked={fitChecklist[`not-${i}`] || false}
                          onChange={() => toggleFitItem(`not-${i}`)}
                          className="mt-1 w-4 h-4 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
                        />
                        <span className="text-sm text-gray-700 group-hover:text-gray-900">{item}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Result Summary */}
              {(() => {
                const goodCount = Object.keys(fitChecklist).filter(k => k.startsWith('good-') && fitChecklist[k]).length;
                const notCount = Object.keys(fitChecklist).filter(k => k.startsWith('not-') && fitChecklist[k]).length;
                const totalChecked = goodCount + notCount;

                if (totalChecked < 3) return null;

                const isGoodFit = goodCount > notCount;

                return (
                  <div className={`rounded-2xl p-6 mb-6 ${isGoodFit ? 'bg-green-100 border border-green-200' : 'bg-violet-100 border border-violet-200'}`}>
                    {isGoodFit ? (
                      <>
                        <h4 className="font-semibold text-green-800 mb-2">Looks like Alli could be a great fit! 🎉</h4>
                        <p className="text-green-700 text-sm mb-4">
                          Based on your selections, you align well with what we look for in practicum students.
                        </p>
                        <Button
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => setStep('placement-result')}
                        >
                          Learn More About Placement
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <h4 className="font-semibold text-violet-800 mb-2">A different path might work better 💜</h4>
                        <p className="text-violet-700 text-sm mb-4">
                          That's okay! You can still get community support and resources while completing practicum elsewhere.
                        </p>
                        <div className="flex flex-wrap gap-3">
                          <Button
                            variant="outline"
                            className="border-violet-300 text-violet-700 hover:bg-violet-50"
                            onClick={() => setStep('core-result')}
                          >
                            Explore Core
                          </Button>
                          <Button
                            className="bg-violet-600 hover:bg-violet-700 text-white"
                            onClick={() => setStep('community-result')}
                          >
                            Explore Community
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              <div className="flex flex-wrap gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => window.open(airtableFormUrl, '_blank')}
                  className="border-blue-200 text-blue-700 hover:bg-blue-50"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Apply for Placement
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStep('community-result')}
                  className="border-rose-300 text-rose-700 hover:bg-rose-100 hover:border-rose-400 hover:text-rose-900"
                >
                  <Users className="w-4 h-4 mr-2" />
                  See Community Options
                </Button>
              </div>
            </div>
          )}

          {/* Core Result */}
          {step === 'core-result' && (
            <div className="animate-fade-in">
              <button
                onClick={() => setStep('main-question')}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back</span>
              </button>

              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-violet-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <div className="inline-flex items-center gap-2 bg-violet-100 px-4 py-2 rounded-full mb-4">
                  <MapPin className="w-4 h-4 text-violet-600" />
                  <span className="text-violet-700 font-medium text-sm">Recommended for You</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Core Membership
                </h2>
                <p className="text-xl text-violet-600 font-semibold mb-2">
                  Get Your Community. Keep Your Placement.
                </p>
              </div>

              <div className="bg-violet-50 rounded-2xl p-6 mb-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Perfect for students who have found a placement elsewhere but still want the support,
                  resources, and connection that comes with being part of a cohort.
                </p>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 justify-center">
                  <span className="text-lg text-gray-400 line-through">$75</span>
                  <span className="text-3xl font-bold text-gray-900">$45</span>
                  <span className="text-gray-500">/month</span>
                  <span className="text-violet-600 text-sm font-medium">Student rate</span>
                </div>
              </div>

              <h4 className="font-semibold text-gray-900 mb-3">What's included:</h4>
              <ul className="space-y-2 mb-8">
                {[
                  "Private Slack community with fellow interns",
                  "Full resource library & clinical tools",
                  "Hours tracking tools",
                  "Monthly knowledge share sessions",
                  "Monthly LevelUp Labs curriculum",
                  "Member discounts on insurance & CE courses",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="flex-1 bg-violet-600 hover:bg-violet-700 text-white"
                  onClick={() => window.location.href = '/between-sessions/join?tier=core&student=true'}
                >
                  Sign Up for Core
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="flex-1 border-rose-200 text-rose-700 hover:bg-rose-50"
                  onClick={() => setStep('community-result')}
                >
                  See Community Tier
                </Button>
              </div>
            </div>
          )}

          {/* Community Result */}
          {step === 'community-result' && (
            <div className="animate-fade-in">
              <button
                onClick={() => setStep('main-question')}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back</span>
              </button>

              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-rose-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-8 h-8 text-white" />
                </div>
                <div className="inline-flex items-center gap-2 bg-rose-100 px-4 py-2 rounded-full mb-4">
                  <Sparkles className="w-4 h-4 text-rose-600" />
                  <span className="text-rose-700 font-medium text-sm">Most Popular</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Community Membership
                </h2>
                <p className="text-xl text-rose-600 font-semibold mb-2">
                  Full Support. Your Placement.
                </p>
              </div>

              <div className="bg-rose-50 rounded-2xl p-6 mb-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Everything in Core plus monthly group supervision and peer support groups.
                  Perfect if you want structured guidance alongside your external placement.
                </p>
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 justify-center">
                  <span className="text-lg text-gray-400 line-through">$125</span>
                  <span className="text-3xl font-bold text-gray-900">$95</span>
                  <span className="text-gray-500">/month</span>
                  <span className="text-rose-600 text-sm font-medium">Student/alumni rate</span>
                </div>
              </div>

              {/* Core features included */}
              <div className="bg-violet-50 rounded-xl p-4 mb-4 border border-violet-100">
                <h4 className="font-semibold text-violet-800 mb-3 text-sm">Core features included:</h4>
                <ul className="space-y-1.5">
                  {[
                    "Private Slack community with fellow interns",
                    "Full resource library & clinical tools",
                    "Hours tracking tools",
                    "Monthly knowledge share sessions",
                    "Monthly LevelUp Labs curriculum",
                    "Member discounts on insurance & CE courses",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <h4 className="font-semibold text-gray-900 mb-3">Plus Community extras:</h4>
              <ul className="space-y-2 mb-8">
                {[
                  "Monthly group supervision (90 min, up to 8)",
                  "Monthly peer support group",
                  "À la carte individual supervision access",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="flex-1 bg-rose-500 hover:bg-rose-600 text-white"
                  onClick={() => window.location.href = '/between-sessions/join?tier=community&student=true'}
                >
                  Sign Up for Community
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="flex-1 border-violet-200 text-violet-700 hover:bg-violet-50"
                  onClick={() => setStep('core-result')}
                >
                  See Core Tier
                </Button>
              </div>
            </div>
          )}

          {/* Placement Result */}
          {step === 'placement-result' && (
            <div className="animate-fade-in">
              <button
                onClick={() => setStep('fit-assessment')}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back</span>
              </button>

              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Briefcase className="w-8 h-8 text-white" />
                </div>
                <div className="inline-flex items-center gap-2 bg-blue-100 px-4 py-2 rounded-full mb-4">
                  <GraduationCap className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-700 font-medium text-sm">Full Practicum Experience</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Alli Placement
                </h2>
                <p className="text-xl text-blue-600 font-semibold mb-2">
                  Real Clients. Real Experience. Full Support.
                </p>
              </div>

              <div className="bg-blue-50 rounded-2xl p-6 mb-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Complete your practicum with real clients through Alli Therapy.
                  You'll get weekly supervision, a diverse caseload, and all the community
                  benefits built in at no extra cost.
                </p>
                <div className="flex items-baseline justify-center gap-2">
                  <span className="text-3xl font-bold text-blue-600">Free</span>
                  <span className="text-gray-500">with placement acceptance</span>
                </div>
              </div>

              <h4 className="font-semibold text-gray-900 mb-3">What's included:</h4>
              <ul className="space-y-2 mb-6">
                {[
                  "Real client work (15-25 diverse clients)",
                  "Weekly dyad supervision included",
                  "All Community tier benefits (group supervision, peer groups, etc.)",
                  "Full resource library & clinical tools",
                  "Monthly LevelUp Labs curriculum",
                  "Work from anywhere in Canada",
                  "Graduate with 200+ clinical hours",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
                <p className="text-amber-800 text-sm">
                  <strong>Note:</strong> Placement requires an application and acceptance.
                  You'll need to commit 25+ hours per week with at least 10 client sessions.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                  onClick={() => window.open(airtableFormUrl, '_blank')}
                >
                  Apply for Placement
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="flex-1 border-rose-200 text-rose-700 hover:bg-rose-50"
                  onClick={() => setStep('community-result')}
                >
                  See Community Option
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
